/*
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 */
import * as React from 'react';

import _isEqual from 'lodash/isEqual';

import OlLayerGroup from 'ol/layer/Group';
import OlLayerImage from 'ol/layer/Image';
import OlLayer from 'ol/layer/Layer';
import OlLayerTile from 'ol/layer/Tile';
import OlMap from 'ol/Map';
import OlSourceImage from 'ol/source/Image';
import OlSource from 'ol/source/Source';
import OlTileSource from 'ol/source/Tile';

import Logger from '@terrestris/base-util/dist/Logger';
import { ArrayTwoOrMore } from '@terrestris/base-util/dist/types';

import MapComponent from '@terrestris/react-geo/dist/Map/MapComponent/MapComponent';

import './ProjectLayerSwitcher.less';

/**
 * @export
 * @interface ProjectLayerSwitcherProps
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
export interface OwnProps {
  /**
   * An optional CSS class which will be added to the wrapping div Element.
   */
  className?: string;
  /**
   * The layers to be available in the switcher.
   */
  layers: ArrayTwoOrMore<OlLayer<OlSource> | OlLayerGroup>;
  /**
   * The main map the layers should be synced with.
   */
  map: OlMap;
}

interface ProjectLayerSwitcherState {
  previewLayer: OlLayer<OlSource> | OlLayerGroup | null;
}

export type ProjectLayerSwitcherProps = OwnProps & React.HTMLAttributes<HTMLDivElement>;

/**
 * Class representing the LayerSwitcher.
 * A basic component to switch between the passed layers.
 * This is most likely to be used for the backgroundlayer.
 *
 * @class LayerSwitcher
 * @extends React.Component
 */
export class ProjectLayerSwitcher extends React.Component<ProjectLayerSwitcherProps, ProjectLayerSwitcherState> {

  /**
   * The internal map of the LayerSwitcher
   * @private
   */
  _map: OlMap | null = null;

  /**
   * The internal index of visible layer in provided layers array. If all passed
   * layers are initially invisible, the first layer in array will be taken as
   * default.
   * @private
   */
  _visibleLayerIndex: number = 0;

  /**
   * Internal layer array, copy of passed layers. Will be used within internal
   * map of the LayerSwitcher.
   * @private
   */
  _layerClones: Array<OlLayer<OlSource> | OlLayerGroup> = [];

  /**
   * Creates the LayerSwitcher.
   *
   * @constructs LayerSwitcher
   */
  constructor(props: ProjectLayerSwitcherProps) {
    super(props);
    this._map = this.getMap();
    this.state = {
      previewLayer: null
    };
  }

  /**
   * A react lifecycle method called when the component did mount.
   */
  componentDidMount() {
    this.setMapLayers();
    this.updateLayerVisibility();
  }

  /**
   * Destroy all map specific stuff when umounting the component.
   *
   * @memberof LayerSwitcher
   */
  componentWillUnMount() {
    if (this._map) {
      this._map.getLayers().clear();
      this._map.setTarget(undefined);
      this._map = null;
    }
  }

  /**
   * Invoked immediately after updating occurs. This method is not called for
   * the initial render.
   *
   * @param prevProps The previous props.
   */
  componentDidUpdate(prevProps: ProjectLayerSwitcherProps) {
    if (!(_isEqual(this.props.layers, prevProps.layers))) {
      this.setMapLayers();
      this.updateLayerVisibility();
    }
  }

  /**
   * Clones a layer
   *
   * @param layer The layer to clone.
   * @returns The cloned layer.
   */
  cloneLayer(layer: OlLayer<OlSource> | OlLayerGroup): OlLayer<OlSource> | OlLayerGroup {
    if (layer instanceof OlLayerGroup) {
      return new OlLayerGroup({
        layers: layer.getLayers().getArray().map(l => {
          if (!(l instanceof OlLayerTile) || !(l instanceof OlLayerGroup)) {
            throw new Error('Layer of layergroup is of unclonable type');
          }
          return this.cloneLayer(l);
        }),
        properties: {
          originalLayer: layer
        },
        ...layer.getProperties()
      });
    } else if (layer instanceof OlLayerTile) {
      const clone = new OlLayerTile({
        source: (layer as OlLayerTile<OlTileSource>).getSource() || undefined,
        properties: {
          originalLayer: layer
        },
        ...layer.getProperties()
      });
      // reset reference to the map instance of original layer
      clone.setMap(null);
      return clone;
    } else if (layer instanceof OlLayerImage) {
      const clone = new OlLayerImage({
        source: (layer as OlLayerImage<OlSourceImage>).getSource() || undefined,
        properties: {
          originalLayer: layer
        },
        ...layer.getProperties()
      });
      // reset reference to the map instance of original layer
      clone.setMap(null);
      return clone;
    } else {
      throw new Error('Layertype not supported');
    }
  };

  /**
   * (Re-)adds the layers to the preview map and sets the visibleLayerIndex.
   *
   */
  setMapLayers = () => {
    const {
      layers
    } = this.props;
    if (layers.length < 2) {
      Logger.warn('LayerSwitcher requires two or more layers.');
    }
    this._map?.getLayers().clear();
    this._layerClones = layers.map((layer, index) => {
      const layerClone = this.cloneLayer(layer);
      if (layerClone.getVisible()) {
        this._visibleLayerIndex = index;
      }
      this._map?.addLayer(layerClone);
      return layerClone;
    });
  };

  /**
   * Sets the visiblity of the layers in the props.map and this._map.
   * Also sets the previewLayer in the state.
   *
   */
  updateLayerVisibility = () => {
    const {
      layers
    } = this.props;
    layers.forEach((l, i) => {
      const clone = this._layerClones.find(lc => lc.get('name') === l.get('name'));
      l.setVisible(this._visibleLayerIndex === i);
      if (clone) {
        clone.setVisible(this._visibleLayerIndex === i);
        if (this._visibleLayerIndex === i) {
          this.setState({ previewLayer: clone });
        }
      }
    });
  };

  /**
   * Constructs this._map
   */
  getMap = (): OlMap => {
    const {
      map
    } = this.props;
    return new OlMap({
      view: map.getView(),
      controls: []
    });
  };

  /**
   * Clickhandler for the overview switch.
   *
   */
  onSwitcherClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.stopPropagation();
    this._map?.getLayers().getArray().forEach((layer, index: number) => {
      if (layer.getVisible()) {
        if (this._layerClones.length - 1 === index) {
          this._visibleLayerIndex = 0;
        } else {
          this._visibleLayerIndex = index + 1;
        }
      }
    });
    this.updateLayerVisibility();
  };

  /**
   * The render function.
   */
  render() {
    const {
      className,
      ...passThroughProps
    } = this.props;

    if (!this._map) {
      return null;
    }

    return (
      <div
        className='project-layer-switcher'
        // {...passThroughProps}
      >
        <div
          className="clip"
          onClick={this.onSwitcherClick}
          role="button"
        >
          <MapComponent
            mapDivId="layer-switcher-map"
            map={this._map}
            role="img"
          />
          {
            this.state.previewLayer &&
            <span className="layer-title">
              {this.state.previewLayer.get('name')}
            </span>
          }
        </div>
      </div>
    );
  }
}

export default ProjectLayerSwitcher;
